import React, { useState, useEffect } from "react";
import "./DriverInfo.css";
import { LogoutButton } from "../Common/LogoutButton";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { apiFetch } from "../../utils/api";
import { toast } from "react-toastify";
import Loader from "../Common/Loader.js";

const defaultDriver = () => ({
  serial: "",
  operatorId: "",
  vehicle: "",
  name: "",
  mobile: "",
  role: "Driver",
  forklift: "",
  status: "Active",
  comments: "",
  vehicleAssigned: "",
});

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DriverInfo = () => {
  const [drivers, setDrivers] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [newDriver, setNewDriver] = useState(defaultDriver());
  const [errors, setErrors] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    role: "",
    forklift: "",
    driverId: "",
    name: "",
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState("latest");
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [pendingSaveIndex, setPendingSaveIndex] = useState(null);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await apiFetch("/api/drivers/getDrivers"); // ✅ same endpoint
        // console.log("driver response dklsfj = ", response);

        if (response.drivers) {
          const normalized = response.drivers.map((d) => ({
            serial: d.DRIVERID || "",
            operatorId: d.OPERATOR_ID || "",
            vehicle: d.VEHICLE || "",
            name: d.NAME || "",
            mobile: d.MOBILE || "",
            role: d.ROLE || "",
            forklift: d.FORKLIFT || "",
            status: d.STATUS || "",
            comments: d.COMMENTS || "",
            createdAt: d.CREATED_AT
              ? new Date(d.CREATED_AT).toLocaleString()
              : "",
            vehicleAssigned: d.VEHICLE_TYPE,
          }));
          setDrivers(normalized);
        } else {
          setFetchError("Failed to fetch drivers");
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setFetchError("Error fetching drivers");
      } finally {
        setLoading(false);
      }
    };

    fetchDrivers();
  }, []);

  const validateDriver = (driver) => {
    const err = {};
    if (!/^[A-Za-z ]+$/.test(driver.name)) {
      err.name = "Name should contain only alphabets";
    }
    if (!/^\d{10}$/.test(driver.mobile)) {
      err.mobile = "Please enter a valid 10 digits mobile number";
    }
    if (driver.role === "Driver" && !/^\d{1,2}$/.test(driver.forklift)) {
      err.forklift = "Please enter a valid 2 digits vehicle number";
    }
    return err;
  };

  const handleChange = (field, value) => {
    let updated = { ...newDriver };
    let updatedErrors = { ...errors };

    if (field === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    } else if (field === "name") {
      value = value.replace(/[^A-Za-z ]/g, "");
    } else if (field === "forklift") {
      value = value.replace(/\D/g, "").slice(0, 2);
    }

    updated[field] = value;
    updatedErrors = validateDriver(updated);
    setNewDriver(updated);
    setErrors(updatedErrors);
  };

  const handleFieldChange = (index, field, value) => {
    // Get the actual driver from filteredDrivers
    const driverToUpdate = filteredDrivers[index];

    // Find this driver in the original drivers array using operatorId as unique identifier
    const originalIndex = drivers.findIndex(
      (d) => d.operatorId === driverToUpdate.operatorId
    );

    if (originalIndex === -1) return; // Safety check

    const updatedDrivers = drivers.map((driver, i) => {
      if (i === originalIndex) {
        let updatedFieldValue = value;

        // Input validation / masking
        if (field === "mobile") {
          updatedFieldValue = updatedFieldValue.replace(/\D/g, "").slice(0, 10);
        } else if (field === "name") {
          updatedFieldValue = updatedFieldValue.replace(/[^A-Za-z ]/g, "");
        } else if (field === "forklift") {
          updatedFieldValue = updatedFieldValue.replace(/\D/g, "").slice(0, 2);
        }

        return {
          ...driver,
          [field]: updatedFieldValue,
        };
      }
      return driver;
    });

    setDrivers(updatedDrivers);
  };

  const confirmAndSave = async (index) => {
    const driverToSave = filteredDrivers[index];
    const originalIndex = drivers.findIndex(
      (d) => d.operatorId === driverToSave.operatorId
    );
    if (originalIndex === -1) return;

    const err = validateDriver(drivers[originalIndex]);
    if (Object.keys(err).length > 0) {
      toast.error("Please fix validation errors before saving.");
      return;
    }

    try {
      // ✅ use apiFetch instead of raw fetch
      const response = await apiFetch("/api/drivers/update", {
        method: "PUT",
        body: JSON.stringify(drivers[originalIndex]),
      });

      // console.log("put resp = ", response);

      // ✅ socket emit remains unchanged
      const socket = io(BASE_URL,{withCredentials: true});
      socket.emit("statusChanged", {
        driverId: drivers[originalIndex].operatorId,
        status: drivers[originalIndex].status,
      });

      toast.success("Driver updated successfully!");

      // ✅ update logged-in user's localStorage if it’s the same driver
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser?.token_no === drivers[originalIndex].operatorId) {
        const updatedUser = {
          ...storedUser,
          name: drivers[originalIndex].name,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.dispatchEvent(new Event("storage")); // 🔔 trigger update
      }

      setEditIndex(null);
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("An error occurred while updating the driver.");
    }
  };

  const handlePopupSave = async () => {
    const validationErrors = validateDriver(newDriver);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // ✅ use apiFetch instead of axios
      const response = await apiFetch("/api/drivers/add", {
        method: "POST",
        body: JSON.stringify(newDriver),
      });

      // console.log("resp add driv = ", response);

      if (response.operatorId) {
        const operatorId = response.operatorId; // assuming backend sends operatorId
        setDrivers([...drivers, { ...newDriver, serial: operatorId }]);
        setPopupOpen(false);
        setNewDriver(defaultDriver());
        setErrors({});
      } else {
        toast.error(response.error || "Failed to add driver.");
      }
    } catch (error) {
      console.error("Error saving driver:", error);
      toast.error("Server error while saving driver.");
    }
  };

  const applyFilters = () => {
    let filtered = drivers.filter((d) => {
      const statusMatch = filters.status ? d.status === filters.status : true;
      const roleMatch = filters.role ? d.role === filters.role : true;
      const forkliftMatch = filters.forklift
        ? d.forklift === filters.forklift
        : true;
      const driverIdMatch = filters.driverId
        ? String(d.operatorId).includes(filters.driverId)
        : true;
      const nameMatch = filters.name
        ? d.name.toLowerCase().includes(filters.name.toLowerCase())
        : true;

      return (
        statusMatch && roleMatch && forkliftMatch && driverIdMatch && nameMatch
      );
    });

    // Sort logic stays the same
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "latest" ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const filteredDrivers = applyFilters();

  if (loading)
    return (
      <div>
        {" "}
        <Loader />{" "}
      </div>
    );
  if (fetchError) return <div>{fetchError}</div>;

  return (
    <div className="driver-info-container">
      <div className="driver-info-btns">
        <button
          onClick={() => navigate("/maintenance-head")}
          style={{
            backgroundColor: "#007bff",
            color: "#fff",
            padding: "8px 16px",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            zIndex: 1000,
          }}
        >
          ← Back
        </button>
        <LogoutButton />
      </div>

      <h2>Driver Information</h2>

      <button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)}>
        🔍 Filter
      </button>

      {filterOpen && (
        <div className="filter-panel">
          <h4>Filter based on</h4>
          <div className="filter-group">
            <label>Status:</label>
            <div className="circle-options">
              {["Active", "Not Active"].map((option) => (
                <div
                  key={option}
                  className={`circle-option ${
                    filters.status === option ? "selected" : ""
                  }`}
                  onClick={() => setFilters({ ...filters, status: option })}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Sticker Number:</label>
            <input
              maxLength={2}
              value={filters.forklift}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  forklift: e.target.value.replace(/\D/g, "").slice(0, 2),
                })
              }
            />
          </div>

          <div className="filter-group">
            <label>Driver ID:</label>
            <input
              placeholder="e.g. D002"
              value={filters.driverId}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  driverId: e.target.value,
                })
              }
            />
          </div>

          <div className="filter-group">
            <label>Sort by Date:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="latest">Latest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Search by Name:</label>
            <input
              type="text"
              placeholder="Type driver name"
              value={filters.name}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  name: e.target.value,
                })
              }
            />
          </div>

          <div className="filter-actions">
            <button className="apply-btn" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
            <button
              className="cancel-btn"
              onClick={() => {
                setFilterOpen(false);
                setFilters({ status: "", role: "", forklift: "" });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="accordion-list">
        {filteredDrivers.map((driver, index) => (
          <div key={index} className="accordion-item">
            <div
              className="accordion-header"
              onClick={() =>
                setExpandedIndex(index === expandedIndex ? null : index)
              }
            >
              <div className="accordion-title">
                <span className="driver-name">
                  {driver.name || "Unnamed Driver"}
                </span>
                <span className="driver-role">({driver.role})</span>
              </div>
              <span className="accordion-arrow">
                {expandedIndex === index ? "▲" : "▼"}
              </span>
            </div>
            {expandedIndex === index && (
              <div className="accordion-body">
                <div className="accordion-field">
                  <label>Driver Name:</label>
                  {editIndex === index ? (
                    <input
                      value={driver.name}
                      onChange={(e) =>
                        handleFieldChange(index, "name", e.target.value)
                      }
                    />
                  ) : (
                    driver.name
                  )}
                </div>
                <div className="accordion-field">
                  <label>Vehicle Number:</label>
                  {editIndex === index ? (
                    <input
                      value={driver.vehicle}
                      onChange={(e) =>
                        handleFieldChange(index, "vehicle", e.target.value)
                      }
                    />
                  ) : (
                    driver.vehicle
                  )}
                </div>
                <div className="accordion-field">
                  <label>Mobile Number:</label>
                  {editIndex === index ? (
                    <input
                      value={driver.mobile}
                      onChange={(e) =>
                        handleFieldChange(index, "mobile", e.target.value)
                      }
                    />
                  ) : (
                    driver.mobile
                  )}
                </div>
                <div className="accordion-field">
                  <label>Sticker Number:</label>
                  {editIndex === index && driver.role === "Driver" ? (
                    <input
                      value={driver.forklift}
                      onChange={(e) =>
                        handleFieldChange(index, "forklift", e.target.value)
                      }
                    />
                  ) : (
                    driver.forklift
                  )}
                </div>
                <div className="accordion-field">
                  <label>Status:</label>
                  {editIndex === index ? (
                    <select
                      value={driver.status}
                      onChange={(e) =>
                        handleFieldChange(index, "status", e.target.value)
                      }
                    >
                      <option>Active</option>
                      <option>Not Active</option>
                    </select>
                  ) : (
                    driver.status
                  )}
                </div>
                <div className="accordion-field">
                  <label>Comments:</label>
                  {editIndex === index ? (
                    <input
                      value={driver.comments}
                      onChange={(e) =>
                        handleFieldChange(index, "comments", e.target.value)
                      }
                    />
                  ) : (
                    driver.comments
                  )}
                </div>
                <div className="accordion-field">
                  <label>Operator ID:</label>
                  <input value={driver.operatorId} disabled />
                </div>
                <div className="accordion-field">
                  <label>Created At:</label>
                  <input value={driver.createdAt} disabled />
                </div>
                <div className="accordion-field">
                  <label>Vehicle Assigned:</label>
                  {editIndex === index ? (
                    <select
                      value={driver.vehicleAssigned}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "vehicleAssigned",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select</option>
                      <option value="Crane">Crane</option>
                      <option value="Forklift">Forklift</option>
                      <option value="AMR">AMR</option>
                      <option value="Cherry Picker">Cherry Picker</option>
                    </select>
                  ) : (
                    driver.vehicleAssigned
                  )}
                </div>

                <div className="accordion-actions">
                  {editIndex === index ? (
                    <>
                      <button
                        className="save"
                        onClick={(e) => {
                          e.stopPropagation(); // ✅ Prevent accidental collapse
                          setPendingSaveIndex(index);
                          setShowSaveConfirmation(true);
                        }}
                      >
                        💾 Save
                      </button>

                      <button
                        className="cancel"
                        onClick={(e) => {
                          e.stopPropagation(); // ✅ Prevent accidental collapse
                          setEditIndex(null);
                          setExpandedIndex(null); // Optionally collapse after cancel
                        }}
                      >
                        ❌ Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="edit"
                      onClick={(e) => {
                        e.stopPropagation(); // ✅ Prevents collapsing accordion
                        setEditIndex(index);
                      }}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="buttons">
        <button className="add" onClick={() => setPopupOpen(true)}>
          ➕ Add New Driver Info
        </button>
      </div>

      {popupOpen && (
        <div className="popup">
          <div className="popup-content">
            <h3>Add New Driver</h3>

            <label>
              Vehicle Registration Number:
              <input
                value={newDriver.vehicle}
                onChange={(e) => handleChange("vehicle", e.target.value)}
              />
            </label>
            <label>
              Driver Name:
              <input
                value={newDriver.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
              {errors.name && <div className="error">{errors.name}</div>}
            </label>
            <label>
              Mobile Number:
              <input
                value={newDriver.mobile}
                onChange={(e) => handleChange("mobile", e.target.value)}
              />
              {errors.mobile && <div className="error">{errors.mobile}</div>}
            </label>
            <label>
              Role:
              <select
                value={newDriver.role}
                onChange={(e) => handleChange("role", e.target.value)}
              >
                <option>Driver</option>
              </select>
            </label>
            {newDriver.role === "Driver" && (
              <label>
                FM Forklift Number:
                <input
                  value={newDriver.forklift}
                  onChange={(e) => handleChange("forklift", e.target.value)}
                />
                {errors.forklift && (
                  <div className="error">{errors.forklift}</div>
                )}
              </label>
            )}
            <label>
              Status:
              <select
                value={newDriver.status}
                onChange={(e) => handleChange("status", e.target.value)}
              >
                <option>Active</option>
                <option>Not Active</option>
              </select>
            </label>
            <label>
              Comments:
              <input
                value={newDriver.comments}
                onChange={(e) => handleChange("comments", e.target.value)}
              />
            </label>
            <label>
              Vehicle Assigned:
              <select
                value={newDriver.vehicleAssigned}
                onChange={(e) =>
                  handleChange("vehicleAssigned", e.target.value)
                }
              >
                <option value="">Select</option>
                <option value="Crane">Crane</option>
                <option value="Forklift">Forklift</option>
                <option value="AMR">AMR</option>
                <option value="Cherry Picker">Cherry Picker</option>
              </select>
            </label>

            <div className="popup-actions">
              <button className="save" onClick={handlePopupSave}>
                💾 Save
              </button>
              <button className="cancel" onClick={() => setPopupOpen(false)}>
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirmation && (
        <div className="confirmation-modal">
          <div className="confirmation-box">
            <h3>Confirm Changes</h3>
            <p>Are you sure you want to save these changes?</p>
            <div className="confirmation-actions">
              <button
                onClick={() => {
                  confirmAndSave(pendingSaveIndex);
                  setShowSaveConfirmation(false);
                }}
              >
                ✅ Yes
              </button>
              <button onClick={() => setShowSaveConfirmation(false)}>
                ❌ No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverInfo;
